import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import type { IslandProps } from '../registry'
import { Badge, Button, EmptyState, Skeleton } from '../../shared/ui'
import { supabase } from '../../lib/supabase'
import { toast } from '../../lib/toast'
import type { Site } from '../../lib/workspace'
import { pickCurrent, useWorkspaces } from './useWorkspaces'
import { isValidDomain, normaliseDomain } from './validation'
import { BUSINESS_CSS } from './businessStyles'

type SiteKind = Site['kind']

const KINDS: Array<{ value: SiteKind; label: string }> = [
  { value: 'publication', label: 'Publication' },
  { value: 'company', label: 'Company' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
]

const DUPLICATE_MESSAGE = 'That domain is already a site in this business.'

function kindLabel(kind: SiteKind): string {
  return KINDS.find((k) => k.value === kind)?.label ?? 'Other'
}

interface SiteInput {
  name: string
  domain: string
  kind: SiteKind
}

/** Returns the cleaned input, or an error message. */
function checkSite(
  rawName: string,
  rawDomain: string,
  kind: SiteKind,
): { ok: true; value: SiteInput } | { ok: false; error: string } {
  const name = rawName.trim()
  const domain = normaliseDomain(rawDomain)
  if (!name) return { ok: false, error: 'Enter a site name.' }
  if (!domain || !isValidDomain(domain)) {
    return { ok: false, error: 'Enter a domain such as example.com.' }
  }
  return { ok: true, value: { name, domain, kind } }
}

function errorMessage(err: { code?: string; message?: string }): string {
  if (err.code === '23505') return DUPLICATE_MESSAGE
  return err.message || 'Something went wrong. Try again.'
}

export default function SitesSettingsIsland(_props: IslandProps) {
  const { workspaces, status: wsStatus, error: wsError, refetch: refetchWs } = useWorkspaces()
  const current = pickCurrent(workspaces)
  const editable = current?.role === 'owner' || current?.role === 'admin'

  const [sites, setSites] = useState<Site[]>([])
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((n) => n + 1), [])

  const businessId = current?.id ?? null

  useEffect(() => {
    if (wsStatus !== 'success') return
    let cancelled = false
    setStatus('loading')
    void (async () => {
      try {
        const { data, error: qErr } = await supabase
          .from('sites')
          .select('id,workspace_id,name,domain,kind,created_at')
          .order('name')
        if (cancelled) return
        if (qErr) {
          setError(qErr.message)
          setStatus('error')
          return
        }
        setSites((data ?? []) as Site[])
        setError(null)
        setStatus('success')
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Could not load sites.')
        setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [wsStatus, businessId, tick])

  if (wsStatus === 'idle' || wsStatus === 'loading') {
    return (
      <div className="biz-page">
        <style>{BUSINESS_CSS}</style>
        <h2 className="biz-title">Sites</h2>
        <LoadingBlock />
      </div>
    )
  }
  if (wsStatus === 'error' || !current) {
    return (
      <div className="biz-page">
        <style>{BUSINESS_CSS}</style>
        <h2 className="biz-title">Sites</h2>
        <EmptyState
          role="alert"
          title="Could not load your business"
          message={wsError ?? 'Something went wrong.'}
          cta={{ label: 'Retry', onClick: refetchWs }}
        />
      </div>
    )
  }

  return (
    <div className="biz-page">
      <style>{BUSINESS_CSS}</style>
      <h2 className="biz-title">Sites</h2>
      <p className="biz-sub">
        Sites are the websites, publications and events that {current.name} gets contacts from.
      </p>

      {editable && <AddSiteForm businessId={current.id} onAdded={reload} />}
      {!editable && (
        <p className="biz-hint">Only owners and admins can add, change or remove sites.</p>
      )}

      {status === 'loading' && <LoadingBlock />}
      {status === 'error' && (
        <EmptyState
          role="alert"
          title="Could not load sites"
          message={error ?? 'Something went wrong.'}
          cta={{ label: 'Retry', onClick: reload }}
        />
      )}
      {status === 'success' && (
        <div className="table-wrap">
          <table aria-label="Sites">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Kind</th>
                {editable && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sites.length === 0 && (
                <tr className="empty-row">
                  <td colSpan={editable ? 4 : 3}>No sites yet</td>
                </tr>
              )}
              {sites.map((s) => (
                <SiteRow key={s.id} site={s} editable={editable} onChanged={reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function LoadingBlock() {
  return (
    <div className="biz-skeletons" aria-busy="true" aria-label="Loading">
      <Skeleton width="40" />
      <Skeleton />
      <Skeleton width="70" />
    </div>
  )
}

// ── add ──────────────────────────────────────────────────────────────────

function AddSiteForm({ businessId, onAdded }: { businessId: string; onAdded: () => void }) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [kind, setKind] = useState<SiteKind>('publication')
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    const checked = checkSite(name, domain, kind)
    if (!checked.ok) {
      setFormError(checked.error)
      return
    }
    setFormError(null)
    setBusy(true)
    try {
      const { error } = await supabase
        .from('sites')
        .insert({ workspace_id: businessId, ...checked.value })
      if (error) {
        const msg = errorMessage(error)
        setFormError(msg)
        toast(msg, 'error')
        return
      }
      toast('Site added', 'success')
      setName('')
      setDomain('')
      setKind('publication')
      onAdded()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not add the site.'
      setFormError(msg)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="biz-form" onSubmit={(e) => void onSubmit(e)} noValidate>
      <div className="biz-field">
        <label className="biz-label" htmlFor="site-new-name">
          Site name
        </label>
        <input
          id="site-new-name"
          className="settings-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="biz-field">
        <label className="biz-label" htmlFor="site-new-domain">
          Domain
        </label>
        <input
          id="site-new-domain"
          className="settings-input"
          type="text"
          placeholder="example.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
        />
      </div>
      <div className="biz-field">
        <label className="biz-label" htmlFor="site-new-kind">
          Kind
        </label>
        <select
          id="site-new-kind"
          className="form-select"
          value={kind}
          onChange={(e) => setKind(e.target.value as SiteKind)}
        >
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </div>
      <Button type="submit" disabled={busy}>
        Add site
      </Button>
      {formError && (
        <p className="biz-error" role="alert">
          {formError}
        </p>
      )}
    </form>
  )
}

// ── row: view, edit, delete ──────────────────────────────────────────────

function SiteRow({
  site,
  editable,
  onChanged,
}: {
  site: Site
  editable: boolean
  onChanged: () => void
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'confirm'>('view')
  const [name, setName] = useState(site.name)
  const [domain, setDomain] = useState(site.domain)
  const [kind, setKind] = useState<SiteKind>(site.kind)
  const [rowError, setRowError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function startEdit() {
    setName(site.name)
    setDomain(site.domain)
    setKind(site.kind)
    setRowError(null)
    setMode('edit')
  }

  async function saveEdit() {
    if (busy) return
    const checked = checkSite(name, domain, kind)
    if (!checked.ok) {
      setRowError(checked.error)
      return
    }
    setRowError(null)
    setBusy(true)
    try {
      const { error } = await supabase.from('sites').update(checked.value).eq('id', site.id)
      if (error) {
        const msg = errorMessage(error)
        setRowError(msg)
        toast(msg, 'error')
        return
      }
      toast('Site saved', 'success')
      setMode('view')
      onChanged()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save the site.'
      setRowError(msg)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete() {
    if (busy) return
    setBusy(true)
    try {
      const { error } = await supabase.from('sites').delete().eq('id', site.id)
      if (error) {
        const msg = errorMessage(error)
        setRowError(msg)
        toast(msg, 'error')
        setMode('view')
        return
      }
      toast('Site deleted', 'success')
      onChanged()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not delete the site.'
      setRowError(msg)
      toast(msg, 'error')
      setMode('view')
    } finally {
      setBusy(false)
    }
  }

  if (mode === 'edit') {
    return (
      <tr>
        <td>
          <input
            className="settings-input"
            type="text"
            aria-label={`Name for ${site.name}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </td>
        <td>
          <input
            className="settings-input"
            type="text"
            aria-label={`Domain for ${site.name}`}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </td>
        <td>
          <select
            className="form-select"
            aria-label={`Kind for ${site.name}`}
            value={kind}
            onChange={(e) => setKind(e.target.value as SiteKind)}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </td>
        <td>
          <div className="biz-actions">
            <Button onClick={() => void saveEdit()} disabled={busy}>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setMode('view')} disabled={busy}>
              Cancel
            </Button>
          </div>
          {rowError && (
            <p className="biz-error" role="alert">
              {rowError}
            </p>
          )}
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td>{site.name}</td>
      <td>{site.domain}</td>
      <td>
        <Badge>{kindLabel(site.kind)}</Badge>
      </td>
      {editable && (
        <td>
          {mode === 'confirm' ? (
            <div className="biz-actions">
              <span className="biz-hint">Delete {site.name}?</span>
              <Button
                variant="danger"
                aria-label={`Confirm delete ${site.name}`}
                onClick={() => void confirmDelete()}
                disabled={busy}
              >
                Delete
              </Button>
              <Button variant="secondary" onClick={() => setMode('view')} disabled={busy}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="biz-actions">
              <Button variant="secondary" aria-label={`Edit ${site.name}`} onClick={startEdit}>
                Edit
              </Button>
              <Button
                variant="danger"
                aria-label={`Delete ${site.name}`}
                onClick={() => setMode('confirm')}
              >
                Delete
              </Button>
            </div>
          )}
          {rowError && (
            <p className="biz-error" role="alert">
              {rowError}
            </p>
          )}
        </td>
      )}
    </tr>
  )
}
